import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Hexagon, ArrowRight } from "lucide-react";

const pub = axios.create({ baseURL: API });

export default function PublicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    pub.get(`/public/pages/${slug}`)
      .then((r) => setPage(r.data))
      .catch(() => setError("Page not found"));
  }, [slug]);

  if (error) {
    return (
      <Wrap>
        <div className="text-center py-20">
          <h1 className="font-display text-3xl">Page not found</h1>
          <p className="text-stone-500 mt-2">It may have been unpublished or moved.</p>
          <Link to="/" className="inline-block mt-6 text-amber-700 hover:underline">← Back home</Link>
        </div>
      </Wrap>
    );
  }
  if (!page) return <Wrap><div className="py-20 text-center text-stone-400">Loading…</div></Wrap>;

  return (
    <Wrap>
      <article className="max-w-3xl mx-auto px-6 py-16" data-testid={`public-page-${page.slug}`}>
        {page.blocks.map((b) => <BlockRenderer key={b.id} block={b} />)}
      </article>
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-yellow-400 grid place-items-center">
              <Hexagon className="w-4 h-4 text-amber-900" strokeWidth={2.5} />
            </div>
            <span className="font-display text-sm font-semibold">Hive <span className="text-yellow-500">@</span> Buzzzillion</span>
          </Link>
        </div>
      </header>
      {children}
    </div>
  );
}

function BlockRenderer({ block }) {
  const d = block.data || {};
  switch (block.type) {
    case "hero":
      return (
        <section className="py-10 border-b border-stone-200 mb-10" data-testid={`block-${block.id}`}>
          {d.eyebrow && <div className="text-xs uppercase tracking-[0.2em] text-amber-700 font-semibold">{d.eyebrow}</div>}
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-3">{d.title}</h1>
          {d.subtitle && <p className="text-lg text-stone-600 mt-4 leading-relaxed">{d.subtitle}</p>}
        </section>
      );
    case "text":
      return (
        <section className="prose prose-stone my-8 max-w-none whitespace-pre-wrap text-stone-700 leading-relaxed" data-testid={`block-${block.id}`}>
          {d.body}
        </section>
      );
    case "image":
      return (
        <figure className="my-10" data-testid={`block-${block.id}`}>
          <img src={d.url} alt={d.alt || ""} className="w-full rounded-2xl border border-stone-200" />
          {d.caption && <figcaption className="text-sm text-stone-500 mt-2 text-center">{d.caption}</figcaption>}
        </figure>
      );
    case "two_column":
      return (
        <section className="grid sm:grid-cols-2 gap-8 my-10" data-testid={`block-${block.id}`}>
          <div className="whitespace-pre-wrap text-stone-700">{d.left}</div>
          <div className="whitespace-pre-wrap text-stone-700">{d.right}</div>
        </section>
      );
    case "cta":
      return (
        <section className="my-10 p-8 rounded-2xl bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 text-center" data-testid={`block-${block.id}`}>
          <h2 className="font-display text-2xl font-medium">{d.title}</h2>
          {d.subtitle && <p className="text-stone-700 mt-2">{d.subtitle}</p>}
          {d.button_link && (
            <Link to={d.button_link} className="inline-flex items-center gap-1.5 mt-5 px-5 py-2.5 rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300 font-medium">
              {d.button_label || "Get started"} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </section>
      );
    case "faq":
      return (
        <section className="my-10" data-testid={`block-${block.id}`}>
          {d.title && <h2 className="font-display text-2xl font-medium mb-5">{d.title}</h2>}
          <ul className="space-y-4">
            {(d.items || []).map((it, i) => (
              <li key={i} className="border-b border-stone-200 pb-4">
                <div className="font-medium text-stone-900">{it.q}</div>
                <div className="text-stone-700 mt-1 whitespace-pre-wrap">{it.a}</div>
              </li>
            ))}
          </ul>
        </section>
      );
    default:
      return null;
  }
}
